import { observer } from 'mobx-react-lite';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { OmniCampaign } from '@feathr/blackbox';
import { EIntegrationTypes } from '@feathr/blackbox';
import { FormSummaryItem } from '@feathr/components';

import type { ISummaryProps } from './CampaignSummary.types';

/**
 * Read-only summary of the Display/Meta budget split. Only dual-channel Omni
 * campaigns have a split, so it renders nothing otherwise.
 */
const ChannelSplitSummary = observer(({ campaign }: ISummaryProps): JSX.Element | null => {
  const { t } = useTranslation();

  if (!campaign.isOmniCampaign || !(campaign as OmniCampaign).isDualChannel) {
    return null;
  }

  const exposureSettings = campaign.get('exposure_settings');
  const totalBudget = exposureSettings?.target_value ?? 0;
  const allocations = exposureSettings?.channel_split?.allocations;
  const displayPercent = allocations?.find(
    (a) => a.channel === EIntegrationTypes.Tradedesk,
  )?.percentage;
  const metaPercent = allocations?.find((a) => a.channel === EIntegrationTypes.Meta)?.percentage;

  if (displayPercent === undefined || metaPercent === undefined) {
    return null;
  }

  const displayBudget = numeral((totalBudget * displayPercent) / 100).format('$0,0.00');
  const metaBudget = numeral((totalBudget * metaPercent) / 100).format('$0,0.00');

  return (
    <FormSummaryItem
      label={t('Channel Budget Split')}
      name={'summary_channel_split'}
      value={t(
        'Display {{displayPercent}}% ({{displayBudget}}) · Meta {{metaPercent}}% ({{metaBudget}})',
        {
          displayBudget,
          displayPercent,
          metaBudget,
          metaPercent,
        },
      )}
    />
  );
});

ChannelSplitSummary.displayName = 'ChannelSplitSummary';

export default ChannelSplitSummary;
